import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Chip,
  alpha,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useTheme,
  useMediaQuery,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Person as PersonIcon,
  Notes as NotesIcon,
  LocalOffer as DiscountIcon,
  Percent as PercentIcon,
  Payment as PaymentIcon,
  Description as InvoiceIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useStoreInfo } from '../../../hooks/useCompanyInfo';
import { generateSaleTicketPreview } from '../utils/ticketPreviewUtils';
import MultiplePaymentMethodsSection from '../../invoices/components/MultiplePaymentMethodsSection';
import * as documentNoteTemplateService from '../../companysettings/services/documentNoteTemplateService';

import { calcTotals, calcOriginalTotal } from '../utils/totalsCalculator';

// ─── Strip HTML tags from template content ──────────────────────────────────
function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').trim();
}

// ─── Section header helper ────────────────────────────────────────────────────
function SectionHeader({ icon: Icon, label, color = 'primary.main', action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
      <Icon sx={{ fontSize: 15, color }} />
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.6px',
          fontSize: '0.63rem',
        }}
      >
        {label}
      </Typography>
      {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
    </Box>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
function CheckoutDialog({
  open,
  onClose,
  cart = [],
  customer,
  walkInCustomerName: initWalkIn = '',
  isTvaIncluded = true,
  paymentMethods = [],      // [{id, name, label, ...}]
  selectedPaymentMethod: initPM = null,
  paidAmount: initPaidAmount = '',
  notes: initNotes = '',
  useMultiplePayments: initUseMultiple = false,
  paymentMethodsArray: initPMArray = [],
  discount,
  customVATRate = 21,
  receiptNumber = '',
  onWalkInNameChange,
  onFinalize,              // ({ format, action, localData, templateHtml }) => void
  onConvertToInvoice,      // () => void — triggers invoice conversion in PosListPage
  onCustomerChange,        // (customer | null) => void — allows changing customer from within dialog
  onOpenCustomerDialog,    // () => void — opens the CustomerDialog from PosListPage
  vatRates = [],           // available VAT rates for selector
  openDrawerOnPrint: initOpenDrawer = true,
  onApplyVAT,              // (rate: number) => void
  onApplyDiscount,         // ({ type, value, amount }) => void
  onRemoveDiscount,        // () => void
  container,               // DOM node for fullscreen POS support
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { storeInfo } = useStoreInfo();

  // ─── Local form state ──────────────────────────────────────────────────────
  const [localPM, setLocalPM] = useState(initPM);
  const [localPaidAmount, setLocalPaidAmount] = useState(initPaidAmount);
  const [localNotes, setLocalNotes] = useState(initNotes);
  const [localUseMultiple, setLocalUseMultiple] = useState(initUseMultiple);
  const [localPMArray, setLocalPMArray] = useState(initPMArray);
  const [localWalkIn, setLocalWalkIn] = useState(initWalkIn);
  const [pmError, setPmError] = useState(false);

  // ─── Local customer + controls state ───────────────────────────────────────────
  const [localCustomer, setLocalCustomer] = useState(customer || null);
  const [localOpenDrawer, setLocalOpenDrawer] = useState(initOpenDrawer);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [printEnabled, setPrintEnabled] = useState(true);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [discountInputVal, setDiscountInputVal] = useState('');
  const [discountInputType, setDiscountInputType] = useState('percentage');

  // ─── Note templates ────────────────────────────────────────────────────────
  const [noteTemplates, setNoteTemplates] = useState([]);
  const [selectedNoteTemplate, setSelectedNoteTemplate] = useState('');
  const [iframeHeight, setIframeHeight] = useState(400);

  // Reset preview height whenever the cart size changes so the next onLoad
  // measurement always starts from a known-small baseline.
  useEffect(() => {
    setIframeHeight(400);
  }, [cart.length]); // eslint-disable-line

  // Re-initialize local state each time dialog opens
  useEffect(() => {
    if (open) {
      setLocalPM(initPM);
      setLocalPaidAmount(initPaidAmount);
      setLocalNotes(initNotes);
      setLocalUseMultiple(initUseMultiple);
      setLocalPMArray(initPMArray);
      setLocalWalkIn(initWalkIn);
      setPmError(false);
      setLocalCustomer(customer || null);
      setLocalOpenDrawer(initOpenDrawer);
      setPrintEnabled(true);
      setShowDiscountForm(false);
      setDiscountInputVal('');
    }
  // open is the only intended dep — init props are intentionally read once on open
  }, [open]); // eslint-disable-line

  // Sync localCustomer when parent customer changes (e.g. after CustomerDialog selection)
  useEffect(() => {
    setLocalCustomer(customer || null);
    if (customer) setLocalWalkIn('');
  }, [customer]);

  // Fetch Ticket note templates once on first open
  useEffect(() => {
    if (!open) return;
    documentNoteTemplateService.getTemplatesByType('Ticket')
      .then((data) => {
        const templates = data?.payload || data || [];
        setNoteTemplates(templates);
        // Always auto-select the default (or first) template on open
        if (templates.length > 0) {
          const def = templates.find((t) => t.IsDefault) || templates[0];
          setSelectedNoteTemplate(def.Id);
          const plain = stripHtml(def.NoteContent);
          setLocalNotes(plain.slice(0, 1000));
        }
      })
      .catch(() => {}); // silently ignore if endpoint unavailable
  }, [open]); // eslint-disable-line

  // ─── Totals ────────────────────────────────────────────────────────────────
  const { subtotal, discountAmount, totalVAT, totalIncl } = useMemo(
    () => calcTotals(cart, discount, customVATRate, isTvaIncluded),
    [cart, discount, customVATRate, isTvaIncluded]
  );
  
  // Calculate original cart total (before discount) for discount validation
  const originalCartTotal = useMemo(() => calcOriginalTotal(cart, isTvaIncluded), [cart, isTvaIncluded]);
  
  const isCash = (localPM?.Name || '').toUpperCase().includes('CASH');

  // Auto-set paid amount to total (for all payment methods)
  useEffect(() => {
    // Always set paid amount to total when dialog opens or total changes
    setLocalPaidAmount(String(totalIncl));
  }, [totalIncl]);

  // ─── Live preview HTML ─────────────────────────────────────────────────────
  const previewHtml = useMemo(() => {
    return generateSaleTicketPreview({
      cart,
      customer: localCustomer,
      storeInfo,
      receiptNumber,
      paidAmount: localPaidAmount,
      paymentMethod: localPM,
      allPaymentMethods: paymentMethods,
      useMultiplePayments: localUseMultiple,
      paymentMethodsArray: localPMArray,
      discount,
      customVATRate,
      notes: localNotes,
      walkInCustomerName: localCustomer ? '' : localWalkIn,
      isTvaIncluded,
    });
  }, [
    storeInfo, cart, localCustomer, localPM, localPaidAmount, receiptNumber,
    discount, customVATRate, localNotes, localUseMultiple, localPMArray,
    paymentMethods, localWalkIn, isTvaIncluded,
  ]);

  // ─── Payment method select handler ─────────────────────────────────────────
  const handlePMSelect = (e) => {
    setPmError(false);
    const val = e.target.value;
    if (val === 'MULTIPLE') {
      setLocalUseMultiple(true);
      setLocalPM(null);
      return;
    }
    const found = paymentMethods.find((pm) => pm.id === val);
    if (found) {
      setLocalPM({ Id: found.id, Name: found.name || found.label });
      if (localUseMultiple) setLocalUseMultiple(false);
    }
  };

  // ─── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add products to cart');
      return false;
    }
    if (localUseMultiple) {
      if (!localPMArray || localPMArray.length === 0) {
        toast.error(t('please_add_payment_methods') || 'Add at least one payment method');
        return false;
      }
      const totalPaid = localPMArray.reduce(
        (s, pm) => s + (parseFloat(pm.amount) || 0), 0
      );
      const remaining = Math.round((totalIncl - totalPaid) * 100) / 100;
      if (Math.abs(remaining) > 0.01) {
        toast.error(
          remaining > 0
            ? `${t('payment_incomplete') || 'Payment incomplete'} — €${remaining.toFixed(2)} remaining`
            : `${t('overpayment') || 'Overpayment'} — €${Math.abs(remaining).toFixed(2)} excess`
        );
        return false;
      }
    } else {
      if (!localPM?.Id) {
        setPmError(true);
        toast.error(t('please_select_payment_method') || 'Please select a payment method');
        return false;
      }
    }
    setPmError(false);
    return true;
  };

  // ─── Print confirm ─────────────────────────────────────────────────────────
  const handlePrint = () => {
    if (!validate()) return;

    const localData = {
      paymentMethod: localUseMultiple ? null : localPM,
      paidAmount: localPaidAmount,
      notes: localNotes,
      useMultiple: localUseMultiple,
      paymentMethodsArray: localPMArray,
      walkInCustomerName: localWalkIn,
      customer: localCustomer,
      openDrawerOnPrint: localOpenDrawer,
      // ✅ Pass pre-calculated totals from dialog
      calculatedTotals: {
        subtotal,
        discountAmount,
        totalVAT,
        totalIncl,
      },
    };

    if (onWalkInNameChange && localWalkIn !== initWalkIn) {
      onWalkInNameChange(localWalkIn);
    }

    if (onFinalize) {
      onFinalize({
        format: 'receipt',
        action: printEnabled ? 'print' : 'register_only',
        localData,
        templateHtml: previewHtml,
      });
    }
  };

  // ─── Convert to Invoice ────────────────────────────────────────────────────
  const handleInvoice = () => {
    if (cart.length === 0) {
      toast.error(t('please_add_products') || 'Please add products to cart');
      return;
    }
    setConvertConfirmOpen(true);
  };

  const handleInvoiceConfirm = () => {
    setConvertConfirmOpen(false);
    onClose();
    if (onConvertToInvoice) onConvertToInvoice();
  };

  const handleInvoiceCancel = () => {
    setConvertConfirmOpen(false);
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
    <Dialog
      open={open}
      onClose={(event, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      container={container}
      style={container ? { position: 'absolute' } : undefined}
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '88vh',
          maxHeight: isMobile ? '100%' : '88vh',
          borderRadius: isMobile ? 0 : 2.5,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* ── Title bar ── */}
      <Box
        sx={{
          flexShrink: 0,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          px: 3,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptIcon sx={{ fontSize: 22 }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1, fontSize: '1.05rem' }}>
              {t('checkout') || 'Checkout'}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
              #{receiptNumber}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.15) } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Two-panel body ── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* ══ LEFT PANEL ══════════════════════════════════════════════════════ */}
        <Box
          sx={{
            flex: isMobile ? 'none' : '0 0 60%',
            width: isMobile ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            borderRight: isMobile ? 'none' : '1px solid',
            borderBottom: isMobile ? '2px solid' : 'none',
            borderColor: 'divider',
            overflow: 'hidden',
            bgcolor: 'white',
          }}
        >
          {/* ── Two-column body ── */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column' : 'row', minHeight: 0, overflow: 'hidden' }}>

            {/* Payment column */}
            <Box
              sx={{
                flex: isMobile ? 'none' : '0 0 57%',
                width: isMobile ? '100%' : undefined,
                p: 1.75,
                borderRight: isMobile ? 'none' : '1px solid',
                borderBottom: isMobile ? '1px solid' : 'none',
                borderColor: 'divider',
                overflowY: 'auto',
                bgcolor: 'white',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#667eea', 0.2), borderRadius: 2 },
              }}
            >
              <SectionHeader icon={PaymentIcon} label={t('payment') || 'Payment'} />

              {/* Multiple payment toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={localUseMultiple}
                    onChange={(e) => {
                      setLocalUseMultiple(e.target.checked);
                      setPmError(false);
                      if (!e.target.checked) setLocalPMArray([]);
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption" sx={{ fontSize: '0.78rem', fontWeight: 600 }}>
                    {t('use_multiple_payment_methods') || 'Multiple Payment Methods'}
                  </Typography>
                }
                sx={{ mb: 1.25, display: 'flex' }}
              />

              {!localUseMultiple ? (
                <Box>
                  {/* Payment method card list */}
                  {pmError && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#f44336', fontWeight: 600, mb: 0.75, fontSize: '0.72rem' }}>
                      {t('payment_method_required') || 'Please select a payment method'}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: paymentMethods.length > 3 ? 'repeat(2, 1fr)' : `repeat(${Math.min(paymentMethods.length || 2, 3)}, 1fr)`,
                      gap: 0.75,
                      mb: 1.25,
                    }}
                  >
                    {paymentMethods
                      .filter((pm) => String(pm.id ?? '').toUpperCase() !== 'MULTIPLE' && String(pm.name ?? '').toUpperCase() !== 'MULTIPLE')
                      .map((pm) => {
                      const isSelected = localPM?.Id === pm.id;
                      return (
                        <Box
                          key={pm.id}
                          onClick={() => {
                            setPmError(false);
                            setLocalPM({ Id: pm.id, Name: pm.name || pm.label });
                            if (localUseMultiple) setLocalUseMultiple(false);
                          }}
                          sx={{
                            p: 1,
                            py: 0.875,
                            borderRadius: 1.5,
                            border: '2px solid',
                            borderColor: isSelected ? '#667eea' : (pmError ? alpha('#f44336', 0.4) : '#e8e8e8'),
                            bgcolor: isSelected ? alpha('#667eea', 0.07) : 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                            transition: 'all 0.14s ease',
                            '&:hover': {
                              borderColor: isSelected ? '#5568d3' : '#c0c0c0',
                              bgcolor: isSelected ? alpha('#667eea', 0.1) : alpha('#667eea', 0.02),
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 26,
                              height: 26,
                              borderRadius: 1,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: isSelected ? '#667eea' : '#f0f0f0',
                              flexShrink: 0,
                              transition: 'all 0.14s',
                            }}
                          >
                            <PaymentIcon sx={{ fontSize: 13, color: isSelected ? 'white' : '#9e9e9e' }} />
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: isSelected ? 800 : 500,
                              fontSize: '0.78rem',
                              color: isSelected ? '#5568d3' : 'text.primary',
                              lineHeight: 1.3,
                              flex: 1,
                            }}
                          >
                            {pm.label || pm.name}
                          </Typography>
                          {isSelected && (
                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: '#667eea', flexShrink: 0 }} />
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              ) : (
                <MultiplePaymentMethodsSection
                  paymentMethods={localPMArray}
                  onPaymentMethodsChange={setLocalPMArray}
                  totalAmount={totalIncl}
                  disabled={totalIncl <= 0}
                  paymentMethodOptions={paymentMethods.map((pm) => ({ value: pm.id, label: pm.label || pm.name }))}
                />
              )}

              {/* Settings card: VAT | Discount | Drawer */}
              <Box sx={{ mt: 1.25, p: 1.25, borderRadius: 1.5, bgcolor: '#f8f9fa', border: '1px solid #ebebeb' }}>
                {/* VAT Rate */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.875 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PercentIcon sx={{ fontSize: 13, color: '#667eea' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.74rem', color: '#555' }}>
                      {t('vat_rate') || 'VAT Rate'}
                    </Typography>
                  </Box>
                  <FormControl size="small">
                    <Select
                      value={customVATRate ?? 21}
                      onChange={(e) => onApplyVAT && onApplyVAT(Number(e.target.value))}
                      sx={{
                        fontSize: '0.75rem', height: 28,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d8d8d8' },
                        '& .MuiSelect-select': { py: 0.25, px: 1, fontSize: '0.75rem' },
                        borderRadius: 1,
                      }}
                    >
                      {(vatRates.length > 0 ? vatRates : [0, 6, 12, 21]).map((r) => (
                        <MenuItem key={r} value={r} sx={{ fontSize: '0.78rem' }}>
                          {r}%
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Divider sx={{ my: 0.75 }} />

                {/* Discount */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: showDiscountForm ? 0.75 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <DiscountIcon sx={{ fontSize: 13, color: '#ff9800' }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.74rem', color: '#555' }}>
                      {t('discount') || 'Discount'}
                    </Typography>
                  </Box>
                  {discountAmount > 0 ? (
                    <Chip
                      label={`€${discountAmount.toFixed(2)}`}
                      size="small"
                      onDelete={() => onRemoveDiscount && onRemoveDiscount()}
                      sx={{ bgcolor: alpha('#ff9800', 0.1), color: '#e65100', fontWeight: 700, height: 22, fontSize: '0.72rem', '& .MuiChip-deleteIcon': { color: '#e65100', fontSize: 14 } }}
                    />
                  ) : (
                    <Button
                      size="small"
                      onClick={() => setShowDiscountForm((v) => !v)}
                      sx={{ minWidth: 0, px: 0.875, py: 0.2, fontSize: '0.72rem', textTransform: 'none', color: showDiscountForm ? '#667eea' : 'text.secondary', borderRadius: 1, border: '1px solid', borderColor: showDiscountForm ? alpha('#667eea', 0.4) : 'transparent', bgcolor: showDiscountForm ? alpha('#667eea', 0.06) : 'transparent', '&:hover': { bgcolor: alpha('#667eea', 0.06), borderColor: alpha('#667eea', 0.3) } }}
                    >
                      <AddIcon sx={{ fontSize: 11, mr: 0.25 }} />{t('add') || 'Add'}
                    </Button>
                  )}
                </Box>

                {showDiscountForm && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.75, p: 0.875, bgcolor: alpha('#ff9800', 0.04), borderRadius: 1.25, border: '1px solid', borderColor: alpha('#ff9800', 0.2) }}>
                    <ToggleButtonGroup value={discountInputType} exclusive onChange={(_, v) => v && setDiscountInputType(v)} size="small" sx={{ height: 24 }}>
                      <ToggleButton value="percentage" sx={{ px: 0.75, py: 0, fontSize: '0.68rem', textTransform: 'none', fontWeight: 700, height: 24 }}>%</ToggleButton>
                      <ToggleButton value="fixed" sx={{ px: 0.75, py: 0, fontSize: '0.68rem', textTransform: 'none', fontWeight: 700, height: 24 }}>€</ToggleButton>
                    </ToggleButtonGroup>
                    <TextField
                      size="small" type="number" value={discountInputVal}
                      onChange={(e) => setDiscountInputVal(e.target.value)}
                      sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: 24, fontSize: '0.8rem', borderRadius: 0.875 }, '& .MuiInputBase-input': { py: 0, px: 0.875 } }}
                      inputProps={{ min: 0 }} placeholder="0"
                    />
                    <Button
                      size="small" variant="contained"
                      onClick={() => {
                        const val = parseFloat(discountInputVal);
                        if (!isNaN(val) && val > 0) {
                          const amount = discountInputType === 'percentage' ? Math.round(originalCartTotal * val / 100 * 100) / 100 : val;
                          if (onApplyDiscount) onApplyDiscount({ type: discountInputType, value: val, amount });
                        }
                        setShowDiscountForm(false);
                        setDiscountInputVal('');
                      }}
                      sx={{ height: 24, textTransform: 'none', fontSize: '0.68rem', fontWeight: 700, minWidth: 0, px: 1, py: 0 }}
                    >
                      {t('apply') || 'Apply'}
                    </Button>
                    <IconButton size="small" onClick={() => { setShowDiscountForm(false); setDiscountInputVal(''); }} sx={{ p: 0.2, color: 'text.disabled' }}>
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                )}

                <Divider sx={{ my: 0.75 }} />

                {/* Open Drawer on Print */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.74rem', color: '#bbb' }}>
                    {t('open_drawer_on_print') || 'Open Drawer on Print'}
                  </Typography>
                  <Switch
                    checked={localOpenDrawer}
                    onChange={(e) => setLocalOpenDrawer(e.target.checked)}
                    color="primary"
                    size="small"
                    disabled
                  />
                </Box>
              </Box>

              {/* Convert to Invoice */}
              <Box sx={{ mt: 1.25 }}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<InvoiceIcon sx={{ fontSize: 17 }} />}
                  onClick={handleInvoice}
                  disabled={cart.length === 0}
                  sx={{
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2.25,
                    py: 0.9,
                    fontSize: '0.84rem',
                    border: '2px solid',
                    borderColor: '#4caf50',
                    color: '#2e7d32',
                    bgcolor: alpha('#4caf50', 0.04),
                    '&:hover': {
                      border: '2px solid #2e7d32',
                      bgcolor: alpha('#4caf50', 0.1),
                      boxShadow: '0 3px 10px rgba(76,175,80,0.25)',
                    },
                  }}
                >
                  {t('convert_to_invoice') || 'Convert to Invoice'}
                </Button>
              </Box>
            </Box>

            {/* Customer + Notes column */}
            <Box
              sx={{
                flex: 1,
                p: 1.75,
                overflowY: 'auto',
                bgcolor: '#f8f9fa',
                display: 'flex',
                flexDirection: 'column',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#667eea', 0.2), borderRadius: 2 },
              }}
            >
              {/* Customer */}
              <Box sx={{ mb: 1.75 }}>
                <SectionHeader icon={PersonIcon} label={t('customer') || 'Customer'} />
                {localCustomer ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5, border: '1.5px solid', borderColor: alpha('#667eea', 0.3), bgcolor: alpha('#667eea', 0.04) }}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: alpha('#667eea', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#667eea' }}>
                        {(localCustomer.CompanyName || localCustomer.Name || '?')[0].toUpperCase()}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.84rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {localCustomer.CompanyName || localCustomer.Name}
                      </Typography>
                      {localCustomer.Email && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>{localCustomer.Email}</Typography>
                      )}
                    </Box>
                    <IconButton size="small" onClick={() => { setLocalCustomer(null); if (onCustomerChange) onCustomerChange(null); }} sx={{ color: 'text.disabled', p: 0.25, '&:hover': { color: 'error.main' } }}>
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                    <TextField
                      size="small"
                      sx={{ flex: 1, '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.84rem', bgcolor: 'white' }, '& .MuiInputBase-input': { py: 0.7 } }}
                      placeholder={t('walk_in_name') || 'Walk-in name (optional)'}
                      value={localWalkIn}
                      onChange={(e) => { const v = e.target.value; if (v.length <= 20) setLocalWalkIn(v); }}
                      inputProps={{ maxLength: 20 }}
                      helperText={localWalkIn.length > 0 ? `${localWalkIn.length}/20` : undefined}
                      FormHelperTextProps={{ sx: { textAlign: 'right', mr: 0, mt: 0.2, fontSize: '0.64rem' } }}
                    />
                    <Button
                      variant="outlined" size="small"
                      onClick={() => onOpenCustomerDialog && onOpenCustomerDialog()}
                      sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 700, fontSize: '0.73rem', borderRadius: 1.5, px: 1.25, py: 0.65, mt: 0.1, borderColor: '#667eea', color: '#667eea', '&:hover': { bgcolor: alpha('#667eea', 0.06), borderColor: '#5568d3' } }}
                    >
                      {t('search') || 'Search'}
                    </Button>
                  </Box>
                )}
              </Box>

              {/* Notes */}
              <Box sx={{ flex: 1 }}>
                <SectionHeader icon={NotesIcon} label={t('notes') || 'Notes'} />

                {/* Template selector — shown only when templates exist */}
                {noteTemplates.length > 0 && (
                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <Select
                      displayEmpty
                      value={selectedNoteTemplate}
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedNoteTemplate(id);
                        if (!id) return;
                        const tmpl = noteTemplates.find((tp) => tp.Id === id);
                        if (tmpl) {
                          const plain = stripHtml(tmpl.NoteContent);
                          setLocalNotes(plain.slice(0, 1000));
                        }
                      }}
                      sx={{
                        bgcolor: 'white',
                        fontSize: '0.82rem',
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                        borderRadius: 1.5,
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: '0.82rem', fontStyle: 'italic', color: 'text.secondary' }}>
                        {t('select_template') || 'Select template…'}
                      </MenuItem>
                      {noteTemplates.map((tmpl) => (
                        <MenuItem key={tmpl.Id} value={tmpl.Id} sx={{ fontSize: '0.82rem' }}>
                          {tmpl.TemplateName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  fullWidth size="small" multiline
                  rows={isMobile ? 4 : 8}
                  value={localNotes}
                  onChange={(e) => e.target.value.length <= 1000 && setLocalNotes(e.target.value)}
                  placeholder={t('enter_notes_placeholder') || 'Enter notes for the receipt\u2026'}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${localNotes.length}/1000`}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.83rem', bgcolor: 'white' },
                    '& .MuiFormHelperText-root': { textAlign: 'right', mr: 0 },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* ── Totals summary bar ── */}
          <Box
            sx={{
              flexShrink: 0,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              px: 2,
              py: 1.25,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', minWidth: 0 }}>
              <Box>
                <Typography sx={{ fontSize: '0.6rem', opacity: 0.75, textTransform: 'uppercase', lineHeight: 1.4 }}>
                  {t('items') || 'Items'}
                </Typography>
                <Chip label={cart.reduce((s, i) => s + i.Quantity, 0)} size="small" sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', fontWeight: 800, height: 18, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.75 } }} />
              </Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.3), my: 0.5 }} />
              <Box>
                <Typography sx={{ fontSize: '0.6rem', opacity: 0.75, lineHeight: 1.4 }}>
                  {t('subtotal_excl_vat') || 'Subtotal (Excl. VAT)'}
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>€{subtotal.toFixed(2)}</Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.3), my: 0.5 }} />
              <Box>
                <Typography sx={{ fontSize: '0.6rem', opacity: 0.75, lineHeight: 1.4 }}>
                  {t('vat') || 'VAT'} ({customVATRate ?? 21}%)
                </Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '0.88rem' }}>€{totalVAT.toFixed(2)}</Typography>
              </Box>
              {discountAmount > 0 && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ bgcolor: alpha('#fff', 0.3), my: 0.5 }} />
                  <Box>
                    <Typography sx={{ fontSize: '0.6rem', opacity: 0.75, lineHeight: 1.4 }}>{t('discount') || 'Discount'}</Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#ffcc80' }}>€{discountAmount.toFixed(2)}</Typography>
                  </Box>
                </>
              )}
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 1 }}>
              <Typography sx={{ fontSize: '0.6rem', opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.5px', lineHeight: 1.4 }}>{t('total') || 'Total'}</Typography>
              <Typography sx={{ fontWeight: 900, fontSize: '1.75rem', lineHeight: 1, letterSpacing: '-0.5px' }}>€{totalIncl.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>

        {/* ══ RIGHT PANEL — Ticket Preview ══════════════════════════════════ */}
        <Box
          sx={{
            flex: isMobile ? 'none' : '0 0 40%',
            width: isMobile ? '100%' : undefined,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: isMobile ? 'none' : '1px solid',
            borderTop: isMobile ? '2px solid' : 'none',
            borderColor: 'divider',
            bgcolor: '#dde1e7',
            minHeight: isMobile ? 320 : 0,
          }}
        >
          {/* Preview title bar */}
          <Box
            sx={{
              flexShrink: 0,
              px: 1.5,
              py: 0.75,
              bgcolor: 'white',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <ReceiptIcon sx={{ fontSize: 14, color: '#667eea' }} />
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}
            >
              {t('receipt_preview') || 'Receipt Preview'}
            </Typography>
            <Tooltip title={printEnabled ? (t('print_toggle_on') || 'Print ON — receipt will print on register') : (t('print_toggle_off') || 'Print OFF — only register, no print')} placement="left">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 700, color: printEnabled ? '#667eea' : 'text.disabled' }}>
                  {t('print') || 'Print'}
                </Typography>
                <Switch
                  checked={printEnabled}
                  onChange={(e) => setPrintEnabled(e.target.checked)}
                  size="small"
                  color="primary"
                  sx={{ ml: 0.25 }}
                />
              </Box>
            </Tooltip>
          </Box>

          {/* Scrollable preview area */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: alpha('#667eea', 0.25), borderRadius: 2 },
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                width: '302px',
                bgcolor: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <iframe
                key={`${cart.length}-${discountAmount}`}
                title="receipt-preview"
                srcDoc={previewHtml}
                scrolling="no"
                style={{
                  width: '302px',
                  height: iframeHeight,
                  border: 'none',
                  display: 'block',
                }}
                onLoad={(e) => {
                  const iframe = e.target;
                  // Wait briefly for the iframe layout to fully stabilise before measuring.
                  setTimeout(() => {
                    try {
                      const doc = iframe.contentDocument;
                      if (!doc || !doc.body) return;
                      
                      // Force a reflow to ensure layout is complete
                      doc.body.offsetHeight;
                      
                      // Use the specific receipt container for the most accurate height;
                      // fall back to documentElement.scrollHeight if unavailable.
                      const container = doc.querySelector('.receipt-container');
                      const bodyPad = 20; // body padding: 10px top + 10px bottom
                      
                      let h;
                      if (container) {
                        h = container.offsetHeight + bodyPad;
                      } else {
                        // Use scrollHeight which gives us the full content height
                        h = Math.max(
                          doc.body.scrollHeight,
                          doc.documentElement.scrollHeight,
                          doc.body.offsetHeight,
                          doc.documentElement.offsetHeight
                        );
                      }
                      
                      // Add extra padding to ensure no content is cut off
                      if (h > 100) {
                        setIframeHeight(h + 40);
                      }
                    } catch (error) {
                      console.warn('Failed to measure iframe height:', error);
                      // Fallback: estimate based on cart size
                      setIframeHeight(Math.max(600, cart.length * 60 + 400));
                    }
                  }, 150);
                }}
                sandbox="allow-same-origin"
              />
            </Box>
          </Box>

          {/* ── Register button ── */}
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 1.5,
              bgcolor: 'white',
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <Button
              variant="contained"
              startIcon={<PrintIcon sx={{ fontSize: 17 }} />}
              onClick={handlePrint}
              disabled={cart.length === 0}
              sx={{
                fontWeight: 800,
                textTransform: 'none',
                borderRadius: 2,
                px: 2.75,
                py: 0.9,
                fontSize: '0.84rem',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 3px 10px rgba(102,126,234,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                  boxShadow: '0 5px 16px rgba(102,126,234,0.5)',
                  transform: 'translateY(-1px)',
                },
                '&:disabled': { background: '#e0e0e0', boxShadow: 'none' },
                transition: 'all 0.18s',
              }}
            >
              {t('register') || 'Register'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>

      {/* ── Convert to Invoice Confirmation ── */}
      <Dialog
        open={convertConfirmOpen}
        onClose={handleInvoiceCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', pb: 1 }}>
          {t('convert_to_invoice') || 'Convert to Invoice'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('convert_to_invoice_confirm') || 'You are about to leave the checkout. The cart will be converted to an invoice. Do you want to continue?'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleInvoiceCancel}
            color="inherit"
            sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}
          >
            {t('no') || 'No'}
          </Button>
          <Button
            onClick={handleInvoiceConfirm}
            variant="contained"
            sx={{
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 1.5,
              bgcolor: '#4caf50',
              '&:hover': { bgcolor: '#2e7d32' },
            }}
          >
            {t('yes') || 'Yes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CheckoutDialog;
